import { Dispatch } from 'react-redux';
import { Server } from './constants';
import { StoreState } from '../../reducers';
import { Api } from '../../typings/api';
import { Action } from '../../actions';
import { MultiAppAuthenticationCredentials, AspConnexionStatus, initialCredentials } from './reducer';
import IndexedDBManager from '../../utils/indexedDB/manager';
import ApiUtils from '../../utils/api';
import { migrateAppContainerLocalStorageToIndexedDB, receiveConfig } from '../Gallery/actions';
import UserAgentUtils from '../../utils/userAgent';
import { createNotification } from '../NotificationBar/actions';
import { AuthMethod } from './constants';
import Utils, { ServerStatusCode } from '../../utils';
import UrlUtils from '../../utils/url';
import NativeApi from '../../utils/nativeApi';
import { ActionType, removeDriveItems, resetFetchAllStatus } from '../DriveItemList/actions';
import DriveItemUtils from '../../utils/driveItem';
import RoutesUtils from '../../utils/routes';
import { clearUpdateTreeInterval } from '../OfflineManager/actions';
import { setCustomCSS } from '../Gallery/actions';
import LocalStorageManager from '../../utils/localStorage/manager';
import { invalidateGallerySession, beginGallerySession } from '../../utils/sessionCancellation';
import { persistLogoutSnapshot, stopActiveNativeDownloads } from '../../utils/logoutSessionCleanup';
import { isSynchronizationInProgress } from '../../utils/synchronization';

export enum MultiAppAuthenticationInfosActionKeys {
	RESET_STATUS = '@@multiAppAuthenticationInfos/RESET_STATUS',
	LOGIN_SUCCESS = '@@multiAppAuthenticationInfos/LOGIN_SUCCESS',
	LOGIN_ERROR = '@@multiAppAuthenticationInfos/LOGIN_ERROR',
	COMPANY_ERROR = '@@multiAppAuthenticationInfos/COMPANY_ERROR',
	LOGOUT = '@@multiAppAuthenticationInfos/LOGOUT',
	UPDATE_CLIENT = '@@multiAppAuthenticationInfos/UPDATE_CLIENT',
	UPDATE_CREDENTIALS = '@@multiAppAuthenticationInfos/UPDATE_CREDENTIALS',
	UPDATE_ACCESSIBLE_APPS = '@@multiAppAuthenticationInfos/UPDATE_ACCESSIBLE_APPS',
	UPDATE_AUTH_METHODS = '@@multiAppAuthenticationInfos/UPDATE_AUTH_METHODS',
	UPDATE_SERVER = '@@multiAppAuthenticationInfos/UPDATE_SERVER',
	SET_FORM_VALUE = '@@multiAppAuthenticationInfos/SET_FORM_VALUE',
	SET_SESSION_CHECKING = '@@multiAppAuthenticationInfos/SET_SESSION_CHECKING',
	TOGGLE_CHECK_SESSION_STATUS= '@@multiAppAuthenticationInfos/TOGGLE_CHECK_SESSION_STATUS',
	SET_ANOTHER_USER_LOGIN = '@@multiAppAuthenticationInfos/SET_ANOTHER_USER_LOGIN',
	SET_SESSION_EXPIRED = '@@multiAppAuthenticationInfos/SET_SESSION_EXPIRED',
	SHOW_SYNCHRONIZATION_LOGOUT_POPUP = '@@multiAppAuthenticationInfos/SHOW_SYNCHRONIZATION_LOGOUT_POPUP',
	CLOSE_SYNCHRONIZATION_LOGOUT_POPUP = '@@multiAppAuthenticationInfos/CLOSE_SYNCHRONIZATION_LOGOUT_POPUP',
	DEFER_LOGOUT_AFTER_SYNC = '@@multiAppAuthenticationInfos/DEFER_LOGOUT_AFTER_SYNC',
	CANCEL_SYNCHRONIZATION_LOGOUT = '@@multiAppAuthenticationInfos/CANCEL_SYNCHRONIZATION_LOGOUT',
}

interface ResetLoginStatusAction extends Action {
	type: MultiAppAuthenticationInfosActionKeys.RESET_STATUS;
}

interface SetNoCompanyStatusAction extends Action {
	type: MultiAppAuthenticationInfosActionKeys.COMPANY_ERROR;
}

interface LoginSuccessfullAction extends Action {
	type: MultiAppAuthenticationInfosActionKeys.LOGIN_SUCCESS;
}

interface LoginFailedAction extends Action {
	type: MultiAppAuthenticationInfosActionKeys.LOGIN_ERROR;
	status: AspConnexionStatus;
}

interface LogoutAction extends Action {
	type: MultiAppAuthenticationInfosActionKeys.LOGOUT;
}

interface UpdateClientAction extends Action {
	type: MultiAppAuthenticationInfosActionKeys.UPDATE_CLIENT;
	clientId: number;
}

interface UpdateAccessibleAppsAction extends Action {
	type: MultiAppAuthenticationInfosActionKeys.UPDATE_ACCESSIBLE_APPS;
	accessibleApps: Api.AspDriveResourceItem[];
}

interface UpdateAuthMethodsAction extends Action {
	type: MultiAppAuthenticationInfosActionKeys.UPDATE_AUTH_METHODS;
	orgAuthMethods: AuthMethod[];
}

interface UpdateCredentialsAction extends Action {
	type: MultiAppAuthenticationInfosActionKeys.UPDATE_CREDENTIALS;
	credentials: MultiAppAuthenticationCredentials;
}

interface UpdateServerAction extends Action {
	type: MultiAppAuthenticationInfosActionKeys.UPDATE_SERVER;
	server: Server;
}

interface SetFormValueAction extends Action {
	type: MultiAppAuthenticationInfosActionKeys.SET_FORM_VALUE;
	input: string;
	value: string;
}

interface SetSessionCheckingAction extends Action {
	type: MultiAppAuthenticationInfosActionKeys.SET_SESSION_CHECKING;
}

interface ToggleCheckSessionStatusAction extends Action {
	type: MultiAppAuthenticationInfosActionKeys.TOGGLE_CHECK_SESSION_STATUS;
	isCheckingSession: boolean;
}

interface SetAnotherUserLoginAction extends Action {
	type: MultiAppAuthenticationInfosActionKeys.SET_ANOTHER_USER_LOGIN;
}

interface SetSessionExpiredAction extends Action {
	type: MultiAppAuthenticationInfosActionKeys.SET_SESSION_EXPIRED;
}

interface ShowSynchronizationLogoutPopupAction extends Action {
	type: MultiAppAuthenticationInfosActionKeys.SHOW_SYNCHRONIZATION_LOGOUT_POPUP;
}

interface CloseSynchronizationLogoutPopupAction extends Action {
	type: MultiAppAuthenticationInfosActionKeys.CLOSE_SYNCHRONIZATION_LOGOUT_POPUP;
}

interface DeferLogoutAfterSyncAction extends Action {
	type: MultiAppAuthenticationInfosActionKeys.DEFER_LOGOUT_AFTER_SYNC;
}

interface CancelSynchronizationLogoutAction extends Action {
	type: MultiAppAuthenticationInfosActionKeys.CANCEL_SYNCHRONIZATION_LOGOUT;
}

export type MultiAppAuthenticationInfosAction = LoginSuccessfullAction
	| LoginFailedAction
	| LogoutAction
	| UpdateClientAction
	| UpdateAccessibleAppsAction
	| UpdateAuthMethodsAction
	| ResetLoginStatusAction
	| SetNoCompanyStatusAction
	| UpdateCredentialsAction
	| UpdateServerAction
	| SetFormValueAction
	| SetSessionCheckingAction
	| ToggleCheckSessionStatusAction
	| SetAnotherUserLoginAction
	| SetSessionExpiredAction
	| ShowSynchronizationLogoutPopupAction
	| CloseSynchronizationLogoutPopupAction
	| DeferLogoutAfterSyncAction
	| CancelSynchronizationLogoutAction;

interface UpdateMultiAppCredentialsArgs {
	login?: string;
	password?: string;
	company?: string;
	method?: AuthMethod;
	token?: string;
	lastApiCallTimestamp?: number;
}


export const initializeMultiAppAuthentication = () => (dispatch: Dispatch<StoreState>, getState: () => StoreState): void => {
	UserAgentUtils.isOnlinePromise()// needed because isOnline might not be initialized yet
		.then(isOnline => {
			// Check if autologin should be prevented (e.g., after explicit logout)
			IndexedDBManager.getPreventAutologin().then(shouldPreventAutologin => {
				if (shouldPreventAutologin) {
					// User explicitly logged out, don't autologin
					return;
				}

				IndexedDBManager.getGalleryConfig().then(previousGalleryConfig => {
					if (!isOnline) {
						if (previousGalleryConfig) {
							dispatch(initGallery(previousGalleryConfig)).then();
							return;
						}

						console.warn('OFFLINE: NO cached config found!');
						dispatch(createNotification('info.offline_login'));
						return;
					}

					IndexedDBManager.getMultiAppCredentials().then(storedCredentials => {
						dispatch(getServer())
							.then(async () => {
								const { multiAppAuthenticationInfos: { credentials: stateCredentials } } = getState();
								const credentials = stateCredentials || storedCredentials;

								if (previousGalleryConfig && credentials) {
									const { method, company } = credentials;

									if (method === AuthMethod.Saml) {
										dispatch(orgLogin(previousGalleryConfig, company)).then();
									} else {
										try {
											const loginData = await ApiUtils.refreshSession();

											if (loginData.login) {
												// Clear prevent autologin flag on successful login
												IndexedDBManager.setPreventAutologin(false).then();
												dispatch(finishLogin(loginData, credentials, true)).then();
											}
										} catch (error) {
											dispatch({ type: MultiAppAuthenticationInfosActionKeys.LOGIN_ERROR, status: error });
										}
									}
								}
							});
					});
				});
			});
		});
};

export const updateCredentials = (updateCredentialsArgs: UpdateMultiAppCredentialsArgs) => async (dispatch: Dispatch<StoreState>, getState: () => StoreState): Promise<void> => {
	const { multiAppAuthenticationInfos: { credentials } } = getState();
	let newCredentials = credentials || initialCredentials;

	Object.entries(updateCredentialsArgs).forEach(([key, value]) => {
		if (value !== undefined) {
			newCredentials[key] = value;
		}
	});

	// Update stored credentials in the background, otherwise changes actions order after login. Refactoring needed to ensure exact order of actions execution
	updateStoredCredentials(newCredentials).then();

	dispatch({
		type: MultiAppAuthenticationInfosActionKeys.UPDATE_CREDENTIALS,
		credentials: newCredentials,
	});
};

const updateStoredCredentials = async (newCredentials: MultiAppAuthenticationCredentials) => {
	const storedCredentials = await IndexedDBManager.getMultiAppCredentials();

	if (storedCredentials && storedCredentials.token && storedCredentials.token !== newCredentials.token) {
		storedCredentials.token = newCredentials.token;
		IndexedDBManager.setMultiAppCredentials(storedCredentials).then();
	}
};


export const getAuthMethods = (company: string) => (dispatch: Dispatch<StoreState>): Promise<AuthMethod[]> => {
	return ApiUtils.getOrgAuthMethods(company)
		.then(response => {
			const { orgAuthMethods } = response;

			if (orgAuthMethods) {
				dispatch(updateCredentials({ company }));
				dispatch({ type: MultiAppAuthenticationInfosActionKeys.UPDATE_AUTH_METHODS, orgAuthMethods });

				return Promise.resolve(orgAuthMethods);
			} else {
				return Promise.reject(AspConnexionStatus.NO_COMPANY_ERROR);
			}
		})
		.catch(() => Promise.reject(AspConnexionStatus.NO_COMPANY_ERROR));
};

export const signIn = (credentials: MultiAppAuthenticationCredentials, shouldSaveCredentials: boolean) => (dispatch: Dispatch<StoreState>): Promise<void> => {
	if (!shouldSaveCredentials) {
		IndexedDBManager.removeMultiAppCredentials().then();
	}

	return ApiUtils.loginToAsp(credentials, shouldSaveCredentials)
		.catch(async () => {
			IndexedDBManager.removeMultiAppCredentials().then();
			return Promise.reject(AspConnexionStatus.AUTH_ERROR);
		})
		.then(response => {
			dispatch(finishLogin(response, credentials, shouldSaveCredentials));
		})
		.catch(reason => {
			dispatch({ type: MultiAppAuthenticationInfosActionKeys.LOGIN_ERROR, status: reason });
			return Promise.reject(reason);
		});
};

const finishLogin = (
	loginData: Api.AppLoginResponse,
	credentials: MultiAppAuthenticationCredentials,
	shouldSaveCredentials: boolean,
	isRefresh: boolean = false,
) => async (dispatch: Dispatch<StoreState>): Promise<void> => {
	const { token, login, associatedApps, clientId, oneSessPerUser, org, email } = loginData;
	const { method, company } = credentials;

	if (associatedApps === undefined) {
		dispatch(createNotification('error.no_application_available'));
		return Promise.reject(AspConnexionStatus.NO_APP_ERROR);
	}

	const storedMultiAppCredentials = await IndexedDBManager.getMultiAppCredentials();
	const updatedOrg = isRefresh && storedMultiAppCredentials ? storedMultiAppCredentials.company : org;
	const userName = method === AuthMethod.Webpublication && !!updatedOrg ? email : login;
	const companyName = method === AuthMethod.Webpublication ? updatedOrg : company;

	if (shouldSaveCredentials) {
		IndexedDBManager.setMultiAppCredentials({
			method,
			login: userName,
			company: companyName,
			token,
		}).then();
		// Clear prevent autologin flag on successful login
		IndexedDBManager.setPreventAutologin(false).then();
	}

	dispatch(updateCredentials({
		...credentials,
		login: userName,
		company: companyName,
		token,
		lastApiCallTimestamp: Date.now(),
	}));

	if (oneSessPerUser) {
		dispatch({ type: MultiAppAuthenticationInfosActionKeys.SET_SESSION_CHECKING });
	}

	dispatch({ type: MultiAppAuthenticationInfosActionKeys.UPDATE_CLIENT, clientId });
	dispatch({ type: MultiAppAuthenticationInfosActionKeys.UPDATE_ACCESSIBLE_APPS, accessibleApps: associatedApps });

	if (associatedApps.length === 1) {
		const { clientId, globalId } = associatedApps[0];
		dispatch(loadGallery(clientId, globalId, shouldSaveCredentials));
	}

	return associatedApps.length > 0
		? Promise.resolve()
		: Promise.reject(AspConnexionStatus.NO_APP_ERROR);
};

export const requestSignOut = () => (dispatch: Dispatch<StoreState>, getState: () => StoreState): void => {
	const { offlineManager } = getState();

	if (UserAgentUtils.isMobileApp && isSynchronizationInProgress(offlineManager)) {
		dispatch({ type: MultiAppAuthenticationInfosActionKeys.SHOW_SYNCHRONIZATION_LOGOUT_POPUP });
		return;
	}

	dispatch(signOut());
};

export const deferSignOutUntilSyncComplete = () =>
	(dispatch: Dispatch<StoreState>, getState: () => StoreState): void => {
		const { offlineManager } = getState();

		if (!isSynchronizationInProgress(offlineManager)) {
			dispatch(signOut());
			return;
		}

		dispatch({ type: MultiAppAuthenticationInfosActionKeys.DEFER_LOGOUT_AFTER_SYNC });
	};

export const cancelSynchronizationLogoutPopup = (): CancelSynchronizationLogoutAction => ({
	type: MultiAppAuthenticationInfosActionKeys.CANCEL_SYNCHRONIZATION_LOGOUT,
});

export const closeSynchronizationLogoutPopup = (): CloseSynchronizationLogoutPopupAction => ({
	type: MultiAppAuthenticationInfosActionKeys.CLOSE_SYNCHRONIZATION_LOGOUT_POPUP,
});

export const signOut = () => async (dispatch: Dispatch<StoreState>, getState: () => StoreState): Promise<void> => {
	dispatch(closeSynchronizationLogoutPopup());
	try {
		await Promise.all([
			ApiUtils.logoutFromAsp(),
			ApiUtils.logoutFromApplication(),
		]);
	} catch (error) {
		if (!UserAgentUtils.isMobileApp) {
			// reload redirects to external login page in web version in case getting error from server (e.g., another user login)
			window.location.reload();
		}
	} finally {
		if (UserAgentUtils.isMobileApp) {
			const stateBeforeLogout = getState();
			invalidateGallerySession();
			stopActiveNativeDownloads(stateBeforeLogout.downloadManager);

			try {
				await persistLogoutSnapshot(stateBeforeLogout);
			} catch (error) {
				console.warn('Failed to persist logout snapshot:', error);
			}
		}

		dispatch(logout());

		if (UserAgentUtils.isMobileApp) {
			await goToHome();
			clearUpdateTreeInterval();
			await IndexedDBManager.removeMultiAppCredentials();
			await IndexedDBManager.removeGalleryConfig();
			await IndexedDBManager.removeProfileInfos();
			// Set flag to prevent autologin after explicit logout
			await IndexedDBManager.setPreventAutologin(true);
			dispatch(clearCachedCSS());
			dispatch(clearPrivateAssets());
			dispatch(resetFetchAllStatus());
		} else {
			const { galleryInfos: { config } } = getState();
			// call app url to redirect to external login page in web version
			const appUrl = UrlUtils.getApplicationUrl(config);
			await Utils.promiseXHR('GET', appUrl);
		}
	}
};

const goToHome = async () => {
	// TODO: Find a cleaner way to wait before navigation to Home happens
	// Currently, if the user is inside his private folder when disconnecting,
	// the app crashes if we delete private assets from driveList before the user reached home
	RoutesUtils.historyPush(RoutesUtils.getPathHome());
	await new Promise(function (resolve: (value: {}) => void) {
		setTimeout(resolve, 300);
	});
};

const clearPrivateAssets = () =>
	(dispatch: Dispatch<StoreState>, getState: () => StoreState) => {
		const {
			galleryInfos: { config },
			driveList,
			driveList: {
				driveItems,
			},
		} = getState();
		const privateDirId = Utils.getPrivateDirId(driveList, config);
		const assetToRemoveIds = Object.keys(driveItems)
			.map(driveItemId => parseInt(driveItemId, 10))
			.filter(driveItemId => DriveItemUtils.isDirDescendantOf(driveItemId, privateDirId, driveItems));
		if (privateDirId) {
			assetToRemoveIds.push(privateDirId);
		}

		dispatch(removeDriveItems(assetToRemoveIds));
	};

const clearCachedCSS = () =>
	(dispatch: Dispatch<StoreState>) => {
		dispatch(setCustomCSS(''));
	};

const logout = (): LogoutAction => ({
	type: MultiAppAuthenticationInfosActionKeys.LOGOUT,
});

export const loadGallery = (clientId: number, galleryId: number, shouldSaveConfig: boolean = false) =>
	(dispatch: Dispatch<StoreState>): Promise<void> => {
		return ApiUtils.getConfig(clientId, galleryId)
			.then(config => dispatch(initGallery(config, shouldSaveConfig)));
	}
;

const initGallery = (config: Api.Config, shouldSaveConfig: boolean = false) => {
	return async (dispatch: Dispatch<StoreState>, getState: () => StoreState) => {
		beginGallerySession();

		const { credentials } = getState().multiAppAuthenticationInfos;
		const login = credentials && credentials.login ? credentials.login : '';

		LocalStorageManager.setGalleryPrefix(config.applicationGallery, login);
		IndexedDBManager.setGalleryPrefix(config.applicationGallery, login);

		dispatch(updateMultiAppConfig(config, shouldSaveConfig));
		dispatch({ type: MultiAppAuthenticationInfosActionKeys.LOGIN_SUCCESS });

		await migrateAppContainerLocalStorageToIndexedDB();
	};
};

const updateMultiAppConfig = (data: Api.Config, shouldSave: boolean) =>
	(dispatch: Dispatch<StoreState>): void => {

		dispatch(receiveConfig(data));
		if (shouldSave) {
			IndexedDBManager.setGalleryConfig(data).then();
		}
	}
;

export const getServer = () => (dispatch: Dispatch<StoreState>, getState: () => StoreState): Promise<void> => {
	const { server } = getState().multiAppAuthenticationInfos;

	if (server) {
		return Promise.resolve();
	}

	return ApiUtils.getAvailableServers()
		.then(servers => {
			let closestServerKey: string = '';
			const serverKeys = Object.keys(servers);

			for (let i = 0; i < serverKeys.length; i++) {
				const key = serverKeys[i];
				if (servers[key].closestServer) {
					closestServerKey = key;
					break;
				}
			}

			IndexedDBManager.setMultiAppServer(closestServerKey as Server).then();
			dispatch({ type: MultiAppAuthenticationInfosActionKeys.UPDATE_SERVER, server: closestServerKey });
		});
};

export const setFormValue = (input: string, value: string) => (dispatch: Dispatch<StoreState>, getState: () => StoreState): void => {
	dispatch({
		type: MultiAppAuthenticationInfosActionKeys.SET_FORM_VALUE,
		input,
		value
	});
};

const toggleCheckSessionStatus = (isCheckingSession: boolean): ToggleCheckSessionStatusAction => ({
	type: MultiAppAuthenticationInfosActionKeys.TOGGLE_CHECK_SESSION_STATUS,
	isCheckingSession,
});

const setAnotherUserLogin = (): SetAnotherUserLoginAction => ({
	type: MultiAppAuthenticationInfosActionKeys.SET_ANOTHER_USER_LOGIN,
});

export const setSessionExpired = (): SetSessionExpiredAction => ({
	type: MultiAppAuthenticationInfosActionKeys.SET_SESSION_EXPIRED,
});

export const checkSession = () => async (dispatch: Dispatch<StoreState>, getState: () => StoreState): Promise<void> => {
	const CHECK_SESSION_DELAY = 5000;
	const {
		multiAppAuthenticationInfos: { isCheckingSession },
	} = getState();

	// to prevent unnecessary calls to api in case several actions are dispatched frequently
	// (for example, typing in forms)
	if (isCheckingSession) {
		return;
	}

	try {
		dispatch(toggleCheckSessionStatus(true));
		await ApiUtils.checkSession();
		setTimeout(() => {
			dispatch(toggleCheckSessionStatus(false));
		}, CHECK_SESSION_DELAY);
	} catch (error) {
		if (error.status === ServerStatusCode.CONFLICT) {
			dispatch(setAnotherUserLogin());
		}
	}
};

export const orgLogin = (config: Api.Config, company?: string) => async (dispatch: Dispatch<StoreState>): Promise<void> => {
	const { action } = await NativeApi.openFrame(UrlUtils.getOrgLoginUrl(config, company));

	if (action.type === ActionType.SAML_LOGIN) {
		const { token } = action;
		return dispatch(signIn({ token, company, method: AuthMethod.Saml }, true));
	}
};

const SERVER_SESSION_DURATION_MINUTES = 30;

export const checkActiveSession = (forceCheck?: boolean) => async (dispatch: Dispatch<StoreState>, getState: () => StoreState): Promise<void> => {
	try {
		const { appInfos, galleryInfos: { isConfigLoaded, config } } = getState();
		const credentials = Utils.getMultiAppCredentials();
		const isLoggedIn = credentials && credentials.login;

		if (!isConfigLoaded || !Utils.isMultiApp(appInfos) || !isLoggedIn) {
			return;
		}

		const shouldCheckSession =
			forceCheck ||
			!ApiUtils.lastApiCallTimestamp ||
			Date.now() - ApiUtils.lastApiCallTimestamp >= (SERVER_SESSION_DURATION_MINUTES - 1) * 60 * 1000;

		if (!shouldCheckSession) {
			return;
		}

		const requestId = 'refresh-session';
		Utils.cancelRequest(requestId);
		const { login, token } = await ApiUtils.refreshSession(requestId);
		const isActiveSession = !!login;

		if (isActiveSession) {
			return await dispatch(updateCredentials({
				token,
				lastApiCallTimestamp: Date.now(),
			}));
		}

		if (Utils.isFreeUrlApp()) {
			return dispatch(freeUrlAppLogin());
		}

		if (isLoggedIn) {
			const { method, company } = credentials as MultiAppAuthenticationCredentials;
			if (method === AuthMethod.Webpublication) {
				dispatch(setSessionExpired());
				return;
			}

			return dispatch(orgLogin(config, company));
		}
	// tslint:disable-next-line
	} catch (error) {}
};

export const freeUrlAppLogin = () => async (dispatch: Dispatch<StoreState>): Promise<void> => {
	try {
		const { token } = await ApiUtils.freeUrlAppLogin();
		return dispatch(updateCredentials({ token }));
	// tslint:disable-next-line
	} catch (error) {}
};



// WEBPACK FOOTER //
// ./node_modules/tslint-loader!./src/containers/MultiAppAuthentication/actions.ts